import { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { ComplianceBanner } from "../compliance/ComplianceBanner";
import { ErrorBoundary } from "@shared/components/ErrorBoundary";
import { MobileOnboardingTour } from "../onboarding/MobileOnboardingTour";
import { DataUseReacknowledgmentModal } from "../onboarding/DataUseReacknowledgmentModal";
import { useMessagingPolling } from "../../lib/useMessagingPolling";
import { useAuth } from "@shared/lib/auth";
import { AdminMobileHeader } from "./AdminMobileHeader";
import { AdminBottomTabBar } from "./AdminBottomTabBar";
import { AdminMoreDrawer } from "./AdminMoreDrawer";
import { MobileBetaBanner } from "./MobileBetaBanner";

const ROUTE_TITLES: Record<string, string> = {
  "/": "Dashboard",
  "/history": "History",
  "/me": "My Profile",
  "/messaging": "Messages",
  "/settings": "Settings",
  "/my-center": "My Center",
  "/employees": "Employees",
};

function getPageTitle(pathname: string): string {
  if (ROUTE_TITLES[pathname]) return ROUTE_TITLES[pathname];
  for (const [prefix, title] of Object.entries(ROUTE_TITLES)) {
    if (prefix !== "/" && pathname.startsWith(prefix)) return title;
  }
  return "Page";
}

export function MobileAdminLayout() {
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const { unreadCount, refresh } = useMessagingPolling();
  const auth = useAuth();

  // Listen for unread count changes
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener("messages:unread-changed", handler);
    return () => window.removeEventListener("messages:unread-changed", handler);
  }, [refresh]);

  // Announce route changes for screen readers + update document title
  useEffect(() => {
    const title = getPageTitle(location.pathname);
    setAnnouncement(`Navigated to ${title}`);
    document.title = `${title} - cradleOS`;
  }, [location.pathname]);

  // Close More drawer on route change
  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname, location.search]);

  return (
    <div className="flex bg-bg h-[100dvh] flex-col overflow-hidden mx-auto w-full relative">
      {/* Skip to content */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-[100] focus:rounded-md focus:bg-accent focus:px-4 focus:py-2 focus:text-white focus:shadow-lg"
      >
        Skip to content
      </a>

      {/* Screen reader route announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>

      <ComplianceBanner />
      <AdminMobileHeader />
      <MobileBetaBanner />

      <main
        id="main-content"
        className="flex-1 overflow-y-auto overscroll-y-none px-4 pb-24 pt-4 bg-bg"
      >
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>

      <AdminBottomTabBar
        unreadCount={unreadCount}
        onMorePress={() => setMoreOpen(true)}
      />

      <AdminMoreDrawer isOpen={moreOpen} onClose={() => setMoreOpen(false)} />

      {(auth.user?.role === "admin" || auth.user?.role === "director") &&
        auth.user && !auth.user.mobile_tour_completed_at && <MobileOnboardingTour />}

      <DataUseReacknowledgmentModal />
    </div>
  );
}
